import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * Mitarbeiter am Telefon (KI-DISPONENT_KONZEPT.md §10.1).
 *
 * Unternehmer und Disponenten erkennt die KI an der Telefonnummer ihres
 * Portal-Zugangs (gepflegt unter „Unternehmen & Portalzugänge"). Ohne PIN
 * nennt sie nur Zahlen ohne Personenbezug; mit PIN auch Namen, Adressen und
 * Details. Die PIN setzt jeder für sich selbst — ein Admin kann sie nur
 * zurücksetzen, nicht kennen.
 *
 * Alles über die Callable manageAiCallerPin; PIN-Hashes verlassen den Server nie.
 */
const callPin = (payload) => httpsCallable(functions, 'manageAiCallerPin')(payload);

const pinChip = ({ pinSet, pinLocked }) => {
  if (pinLocked) return <Chip size="small" color="error" label="PIN gesperrt (30 Min)" />;
  if (pinSet) return <Chip size="small" color="success" variant="outlined" label="PIN gesetzt" />;
  return <Chip size="small" variant="outlined" label="Ohne PIN" />;
};

const AiTelephonyPinPanel = () => {
  const [own, setOwn] = useState(null); // { pinSet, pinLocked, hasPhone }
  const [accounts, setAccounts] = useState(null); // nur für Admins
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message }
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pin, setPin] = useState('');

  const load = useCallback(async () => {
    try {
      const status = await callPin({ op: 'status' });
      setOwn(status.data || null);
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'PIN-Status konnte nicht geladen werden.' });
    }
    try {
      const list = await callPin({ op: 'list' });
      setAccounts(list.data?.accounts || []);
    } catch (_) {
      // Kein Admin: die Übersicht bleibt ausgeblendet.
      setAccounts(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (payload, successMessage) => {
    setBusy(true);
    setFeedback(null);
    try {
      await callPin(payload);
      setFeedback({ severity: 'success', message: successMessage });
      await load();
      return true;
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Speichern fehlgeschlagen.' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSetPin = async () => {
    if (await run({ op: 'set', pin }, 'Ihre Telefon-PIN ist gesetzt.')) {
      setPinDialogOpen(false);
      setPin('');
    }
  };

  return (
    <Box sx={{ px: 3, pt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Mitarbeiter am Telefon
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
        Ruft jemand von der Telefonnummer eines Portal-Zugangs an, erkennt ihn die KI als
        Unternehmer bzw. Disponent statt als Fahrgast und nennt ihm Zahlen zum Betrieb.
        Namen, Adressen und Details zu einzelnen Fahrten nennt sie erst nach der persönlichen
        PIN — die angezeigte Rufnummer allein lässt sich fälschen. Die Nummer pflegen Sie unter
        „Unternehmen &amp; Portalzugänge". Fahrer erkennt die KI an der Nummer im Fahrerprofil.
      </Typography>

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          {own && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
              <Typography variant="body2" component="div" sx={{ flex: 1 }}>
                Ihre Telefon-PIN {pinChip(own)}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => { setPin(''); setPinDialogOpen(true); }} disabled={busy}>
                  {own.pinSet ? 'PIN ändern' : 'PIN festlegen'}
                </Button>
                {own.pinSet && (
                  <Button size="small" color="error" onClick={() => run({ op: 'clear' }, 'Ihre Telefon-PIN wurde entfernt.')} disabled={busy}>
                    Entfernen
                  </Button>
                )}
              </Stack>
            </Stack>
          )}
          {own && !own.hasPhone && (
            <Alert severity="info" sx={{ mb: 1 }}>
              An Ihrem Zugang ist keine Telefonnummer hinterlegt — die KI erkennt Sie deshalb noch nicht.
            </Alert>
          )}

          {accounts && accounts.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                Portal-Zugänge
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Die KI erkennt einen Zugang an seiner Telefonnummer. Fahrer erkennt sie über die Nummer
                im Fahrerprofil; sie stehen hier nicht. Gehört eine Nummer zugleich einem Fahrer, kann der
                Anrufer beides: Lagebild abfragen und als Fahrer den Fahrgast rausklingeln lassen.
              </Typography>
              <List dense disablePadding>
                {accounts.map((account) => (
                  <ListItem
                    key={account.id}
                    disableGutters
                    secondaryAction={(account.pinSet || account.pinLocked) && (
                      <Button
                        size="small"
                        onClick={() => run({ op: 'clear', userId: account.id }, `PIN von ${account.name} zurückgesetzt.`)}
                        disabled={busy}
                      >
                        Zurücksetzen
                      </Button>
                    )}
                    sx={{ pr: 14 }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>{account.name}</span>
                          <Chip size="small" label={account.contactRole === 'owner' ? 'Unternehmer' : 'Disponent'} />
                          {pinChip(account)}
                          {account.alsoDriver && (
                            <Chip size="small" variant="outlined" label="auch Fahrer" />
                          )}
                          {account.duplicatePhone && (
                            <Chip size="small" color="warning" label="Nummer doppelt — wird nicht erkannt" />
                          )}
                        </Stack>
                      }
                      secondary={account.phones.length > 0 ? account.phones.join(' · ') : 'Keine Telefonnummer hinterlegt — wird nicht erkannt'}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </>
      )}

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mt: 1 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Dialog open={pinDialogOpen} onClose={() => !busy && setPinDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Telefon-PIN festlegen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            4 bis 6 Ziffern. Die KI fragt sie ab, bevor sie Namen, Adressen oder Fahrtdetails nennt.
            Nach 5 Fehlversuchen ist sie 30 Minuten gesperrt. Sie steht im Gesprächsprotokoll,
            weil Sie sie aussprechen — wählen Sie keine PIN, die Sie anderswo verwenden.
          </Typography>
          <TextField
            label="Neue PIN"
            type="password"
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'new-password' }}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPinDialogOpen(false)} disabled={busy}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSetPin} disabled={busy || pin.length < 4}>
            PIN festlegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiTelephonyPinPanel;
